import { useMemo } from 'react'
import { useCurrentState, type VisitLifecycleState } from './useCurrentState'
import { useTrackActionHistory } from './useTrackActionHistory'
import { type UseParticipantConnectionStatusesReturnType } from '../../useParticipantConnectionStatuses'
import { type UseDailyDisconnectMethodsReturn } from '../../useDailyDisconnectMethods'

export type UseVisitLifecycleReturnType = ReturnType<typeof useVisitLifecycle>

type Input = { lastJoinAttemptDidConnectWithPatient: boolean } & UseParticipantConnectionStatusesReturnType &
  Pick<UseDailyDisconnectMethodsReturn, 'endVisitCall'>

export function useVisitLifecycle(input: Input) {
  const { currentState, stateHistory, actionHistory, recordAction, callTimedOut } = useCurrentState(input)

  useTrackActionHistory({ ...input, recordAction, currentState, callTimedOut })

  return useMemo(
    () => ({
      /**
       * The current lifecycle state. Only one state is active at a time (mutually exclusive).
       */
      currentState,
      /**
       * Use the `is()` type-safe helper to check if the current state matches any of the provided states.
       * Accepts one or more state names. Returns true if the current state matches any of them.
       * Examples:
       *   `if (is('IDLE')) { ... }`
       *   `if (is('CALL_IS_ACTIVE', 'PROVIDER_CONNECTED_AND_WAITING_FOR_PATIENT_CONNECTION')) { ... }`
       */
      is: (...targetStates: VisitLifecycleState[]) => targetStates.includes(currentState),
      /**
       * Type-safe helper to check if the current state does NOT match any of the provided states.
       * Accepts one or more state names. Returns true if the current state does NOT match any of them.
       * Examples:
       *   `if (not('IDLE')) { ... }`
       *   `if (not('CALL_IS_ACTIVE', 'PROVIDER_CONNECTED_AND_WAITING_FOR_PATIENT_CONNECTION')) { ... }`
       */
      not: (...targetStates: VisitLifecycleState[]) => !targetStates.includes(currentState),
      stateHistory,
      actionHistory,
    }),
    [currentState, stateHistory, actionHistory],
  )
}
